import type { CurrentUser, SessionAdapter } from '@zhiyue/core';

import { BizAppConfig } from 'config/application.manifest';
import {
  createTenant as createTenantApi,
  deleteTenant as deleteTenantApi,
  getCurrentMemberUser,
  getTenantByCode as getTenantByCodeApi,
  getTenantList as getTenantListApi,
  joinTenant as joinTenantApi,
  logout as logoutApi,
  switchTenant as switchTenantApi,
} from '@/services/bff';
import {
  FAMS_DEV_TENANT_UUID,
  readCurrentTenantUuid,
  saveCurrentTenantUuid,
} from './tenantStorage';

const isDev = process.env.NODE_ENV === 'development';

export const sessionAdapter: SessionAdapter = {
  fetchCurrentUser: async () => {
    const user = (await getCurrentMemberUser()) as CurrentUser &
      API.CurrentUser;
    console.log('[memberUuid][frontend] current session user', {
      memberUuid: user.memberUuid,
      userUuid: user.uuid,
      tenantUuid: user.tenantUuid,
      organizationUuid: user.organizationUuid,
    });
    if (isDev) {
      console.debug('[session] fetchCurrentUser resolved tenant', {
        source: 'members/me',
        tenantUuid: user.tenantUuid,
      });
    }
    saveCurrentTenantUuid(user.tenantUuid);
    return user;
  },
  fetchTenants: async () => {
    const result = await getTenantListApi();
    const storedTenantUuid = readCurrentTenantUuid();
    const preferredTenantUuid =
      isDev && result?.some((item) => item.id === FAMS_DEV_TENANT_UUID)
        ? FAMS_DEV_TENANT_UUID
        : storedTenantUuid || result?.[0]?.id;
    if (preferredTenantUuid && preferredTenantUuid !== storedTenantUuid) {
      saveCurrentTenantUuid(preferredTenantUuid);
      if (isDev) {
        console.debug('[session] fetchTenants preferred tenant selected', {
          source: 'tenant-list',
          tenantUuid: preferredTenantUuid,
        });
      }
    } else if (!storedTenantUuid) {
      saveCurrentTenantUuid(result?.[0]?.id);
      if (isDev) {
        console.debug('[session] fetchTenants fallback tenant selected', {
          source: 'tenant-list',
          tenantUuid: result?.[0]?.id || null,
        });
      }
    } else if (isDev) {
      console.debug('[session] fetchTenants keeps existing tenant', {
        tenantUuid: storedTenantUuid,
      });
    }
    return result || [];
  },
  switchTenant: async (organizationUuid: string) => {
    const result = await switchTenantApi(organizationUuid);
    saveCurrentTenantUuid(result?.tenantUuid);
    window.location.reload();
  },
  createTenant: async ({ tenantName }: { tenantName: string }) => {
    const result = await createTenantApi({ tenantName });
    saveCurrentTenantUuid(result?.tenantUuid);
    return result;
  },
  lookupTenantByCode: async (code: string) => getTenantByCodeApi(code),
  joinTenant: async (payload: { tenantUuid: string; nodeId: string }) => {
    const result = await joinTenantApi(payload);
    saveCurrentTenantUuid(result?.tenantUuid);
    return result;
  },
  deleteTenant: async (tenantUuid: string) => {
    const result = await deleteTenantApi(tenantUuid);
    saveCurrentTenantUuid(result?.tenantUuid || null);
    return result;
  },
  logout: async () => {
    const logoutBaseUrl = BizAppConfig.appSsoLogoutUrl;
    const logoutUrl = `${logoutBaseUrl}?redirect_uri=${encodeURIComponent(`${window.location.origin}/user/login`)}`;

    try {
      await logoutApi(BizAppConfig.appSsoClientId as string);
    } catch {
      // Keep local logout flow even when backend logout fails.
    }

    saveCurrentTenantUuid(null);
    return { logoutUrl };
  },
};
